import {
	type AssemblyServiceConfig,
	type AssemblyServiceConfigDtoInput,
	type AssemblyServiceConfigDtoOutput,
	assemblyServiceConfigDtoSchema,
	createLog,
	INJECTED_FEE_CODES,
	LogSeverity,
	OPERATIONS_SECTIONS,
	PricingModel,
	Services,
	type ServicesProvider,
	SETTINGS_GROUPS,
} from '@ekhodealer/ekho-common';
import { Form } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateAssemblyService } from '../../../api/configuration/services/useUpdateAssemblyService';
import { useServiceProgramInfo } from '../../../hooks/useServiceProgramInfo';
import { FormSection } from '../../config-mgmt/FormSection';

/**
 * Helper function to get initial form data from ServicesProvider
 */
function getDefaultValues(
	servicesProvider: ServicesProvider | undefined | null,
): AssemblyServiceConfigDtoInput {
	const assemblyConfig = servicesProvider?.config?.[Services.ASSEMBLY] as
		| AssemblyServiceConfig
		| undefined;

	if (!assemblyConfig?.pricing?.length) {
		return {
			supported: false,
			pricing: [
				{
					model: PricingModel.FLAT_FEE,
					modelDetails: {
						flatFee: 0,
					},
				},
			],
		};
	}

	const firstPricing = assemblyConfig.pricing[0];

	return {
		supported: assemblyConfig.supported ?? false,
		pricing: [firstPricing],
	};
}

export function AssemblyServiceForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const programInfo = useServiceProgramInfo(OPERATIONS_SECTIONS.ASSEMBLY);
	const updateAssemblyServiceMutation = useUpdateAssemblyService();

	// Early return if service is not part of the program
	if (!programInfo.shouldRender) {
		return null;
	}

	// Handle form submit
	const handleSubmit = async (
		data: AssemblyServiceConfigDtoOutput,
	): Promise<{ isSuccess: boolean }> => {
		if (!servicesProvider) return { isSuccess: false };

		const dataToSubmit = {
			supported: data.supported,
			pricing: [
				{
					model: data.pricing[0].model,
					code: INJECTED_FEE_CODES.ASSEMBLY_FEE,
					feeDescription: 'Assembly fee',
					modelDetails: data.pricing[0].modelDetails,
				},
			],
		};

		try {
			await updateAssemblyServiceMutation.mutateAsync(dataToSubmit);
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update assembly service configuration: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	return (
		<FormSection
			sectionPath={{
				group: SETTINGS_GROUPS.OPERATIONS,
				section: OPERATIONS_SECTIONS.ASSEMBLY,
			}}
			form={{
				schema: assemblyServiceConfigDtoSchema,
				defaultValues: getDefaultValues(servicesProvider),
				onSubmit: handleSubmit,
			}}
			title='Assembly'
			description='Set the default assembly fee charged to buyers at checkout. Covers the cost of assembling the vehicle before delivery or pickup.'
			formTitle='Assembly pricing'
			toggleSettings={{
				fieldName: 'supported',
				visibility: 'write',
			}}
			hideDivider>
			<Form.Field.Pricing
				schemaField='pricing'
				supportedModels={[PricingModel.FLAT_FEE, PricingModel.LINEAR_HOURLY]}
				modelLabelText='Assembly Fee'
				modelDescriptionText='Fee charged to the buyer at checkout for assembly. This can be customized per vehicle.'
			/>
		</FormSection>
	);
}
